























import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { Getter } from 'vuex-class';
import { ContactAttributeViewModel } from '@/messaging/models/contactAttributeViewModel';
import FriendlyString from '@/shared/components/FriendlyString.vue';
import EditableText from '@/shared/components/EditableText.vue';
import FriendlyDateTime from '@/shared/components/FriendlyDateTime.vue';
import { ContactViewModel } from '@/features/contacts/models/contactViewModel';
import { ContactAttributeType } from '@/messaging/models/contactAttributeType';
import EditableComponent from '@/shared/components/EditableComponent.vue';
import AttributeIconUtil from '@/shared/util/attributeIconUtil';
import Icon from '@/shared/icons/Icon.vue';
import PageHeader from '@/shared/components/pageHeader/PageHeader.vue';
import appInsights from '@/shared/services/appInsights';
import WorkspaceViewModel from '@/workspaces/models/workspaceViewModel';
import TitledSection from '@/shared/components/TitledSection.vue';

interface ContactAttributeRow {
	id: string;
	type: string;
	system: boolean;
	name: string;
	icon: string | undefined;
	value: any;
	options: string[];
}

@Component({
	components: { TitledSection, EditableComponent, PageHeader, FriendlyString, EditableText, FriendlyDateTime, Icon },
})
export default class ContactDetails extends Vue {
	@Getter public currentWorkspace!: WorkspaceViewModel;
	@Getter public contactAttributes!: ContactAttributeViewModel[];
	@Prop() public value: ContactViewModel;
	public contact: ContactViewModel;

	public data: ContactAttributeRow[] = [];

	public columns = [
		{ name: 'icon', display: '' },
		{ name: 'name', display: 'Name' },
		{ name: 'value', display: 'Value' }
	];

	@Watch('value', {immediate: true, deep: true})
	public onValueChange(value: ContactViewModel) {
		this.contact = value;
		const attributes: ContactAttributeViewModel[] = this.contactAttributes.filter(a => a.id !== 'name');

		attributes.unshift({ id: 'type', name: 'Type', type: ContactAttributeType.Text, system: true } as ContactAttributeViewModel);
		attributes.unshift({ id: 'surname', name: 'Last Name', type: ContactAttributeType.Text, system: true } as ContactAttributeViewModel);
		attributes.unshift({ id: 'givenName', name: 'First Name', type: ContactAttributeType.Text, system: true } as ContactAttributeViewModel);

		this.data = [];
		attributes.forEach(a => this.data.push({
			id: a.id,
			type: this.getHtmlInputTypeForAttribute(a),
			system: a.system,
			name: a.name,
			icon: AttributeIconUtil.getIcon(a.type, a.id, a.system),
			value: this.getAttributeDisplayValue(a),
			options: a.name === 'Type' ? ['Lead', 'User'] : []
		} as ContactAttributeRow));
	}

	public contactUpdated(row: ContactAttributeRow, value: any) {
		appInsights.trackEvent({ name: 'Contact details updated' }, { workspaceId: this.currentWorkspace.id, field: row.id, value });
		if (row.id === 'givenName') {
			this.contact.givenName = value;
		} else if (row.id === 'surname') {
			this.contact.surname = value;
		} else if (row.id === 'type') {
			this.contact.type = value.toLowerCase();
		} else if (row.id === 'email') {
			this.contact.email = value;
		} else if (row.id === 'phoneNumber') {
			this.contact.phoneNumber = value;
		} else if (row.id === 'firstSeen') {
			this.contact.firstSeenUtc = value;
		} else if (row.id === 'lastSeen') {
			this.contact.lastSeenUtc = value;
		} else if (row.id === 'lastContacted') {
			this.contact.lastContactedUtc = value;
		} else if (row.id === 'lastHeardFrom') {
			this.contact.lastHeardFromUtc = value;
		} else {
			Vue.set(this.contact.customAttributes, row.id, value);
		}
		this.emitContact();
	}

	public emitContact() {
		this.$emit('input', this.contact);
	}

	private getHtmlInputTypeForAttribute(attribute: ContactAttributeViewModel) {
		if (attribute.type === 'date' && attribute.system) {
			return 'datetime-local';
		}
		if (attribute.id === 'email') {
			return 'email';
		}

		if (attribute.id === 'phoneNumber') {
			return 'tel';
		}

		if (attribute.id === 'type') {
			return 'select';
		}
		return attribute.type;
	}

	private getAttributeDisplayValue(attribute: ContactAttributeViewModel): any {
		if (attribute.system) {
			return this.getSystemAttributeDisplayValue(attribute);
		} else {
			return this.getCustomAttributeDisplayValue(attribute);
		}
	}

	private getSystemAttributeDisplayValue(attribute: ContactAttributeViewModel) {
		if (attribute.type === ContactAttributeType.Date) {
			const value = (this.value as any)[attribute.id + 'Utc'];
			if (value) {
				return new Date(value);
			}
			return value;
		} else {
			const value = (this.value as any)[attribute.id];
			if (attribute.id === 'type') {
				return value.charAt(0).toUpperCase() + value.slice(1);
			}
			return value;
		}
	}

	private getCustomAttributeDisplayValue(attribute: ContactAttributeViewModel) {
		const value = this.value.customAttributes[attribute.id];
		if (attribute.type === ContactAttributeType.Date && value) {
			return new Date(value);
		}
		return value;
	}
}
